import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClassDashboard, getEnterpriseDashboard, readableApiError, formatClassSubtitle, downloadClassPdf, downloadEnterprisePdf } from '../../api/dashboard';
import { useAuth } from '../../context/AuthContext';
import { canAccessTrainingReco } from '../../auth/roles';
import { scoreBucketTone } from './_styles';
import './dashboard.css';

// scope-aware（class | enterprise）：无班级企业全员看板复用本组件，仅数据源 + 路由前缀 + 头部 meta 切换。
export default function ClassOverviewPage({ scope = 'class' }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const isEnt = scope === 'enterprise';
  const basePath = isEnt ? `/dashboard/enterprises/${id}` : `/dashboard/classes/${id}`;

  const [dlClassLoading, setDlClassLoading] = useState(false);
  const [dlEntLoading, setDlEntLoading] = useState(false);
  const [dlError, setDlError] = useState(null);

  // unmount-safety: trailing setState after async download must not run on unmounted component.
  // 必须在 effect body 把 ref 设回 true —— React 18 StrictMode(dev) 会 mount→cleanup→remount,
  // 只在 cleanup 里设 false 会让 remount 后 ref 永久 false → 下载完成的 setState 被吞 →
  // 按钮永远卡在"下载中…"（用户实测：报告已下完按钮不恢复）。
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setData(null); setError(null);
    (isEnt ? getEnterpriseDashboard(id) : getClassDashboard(id))
      .then(setData)
      .catch((err) => setError(readableApiError(err)));
  }, [id, isEnt]);

  if (error) return <div className="aiq-dash dash-page"><div className="dash-card dash-card--error">{error}</div></div>;
  if (!data) return <div className="aiq-dash dash-page"><div className="dash-card">加载中...</div></div>;

  const { completion, team_avg_score, dimension_avgs, score_distribution } = data;
  const meta = isEnt ? data.enterprise : data.class;
  const title = meta.name;
  const subtitle = isEnt ? `${meta.industry || '未指定行业'} · 全员` : formatClassSubtitle(meta);
  const buckets = score_distribution || [];
  const maxBucketCount = Math.max(...buckets.map((x) => x.count), 1);
  const donePct = completion.total ? Math.round((completion.completed / completion.total) * 100) : 0;

  // 培训建议 tab 仅 enterprise_admin / admin / superadmin 可见（设计文档 4.3.3）
  const tabs = [
    ['', '总览'],
    ['/heatmap', '维度强弱'],
    ['/risks', '风险预警'],
    ['/distribution', '差异分布'],
    // 作答质量（水填）+ 培训建议同 gate：仅 enterprise_admin / admin / superadmin
    ...(canAccessTrainingReco(user) ? [['/response-quality', '作答质量']] : []),
    ...(canAccessTrainingReco(user) ? [['/recommendation', '培训建议']] : []),
  ];

  // 超管/admin 下钻：返回链带 ?enterprise，保住「返回企业列表」面包屑（codex Low #5）。
  const backTo = (!isEnt && ['admin', 'superadmin'].includes(user?.role) && meta?.enterprise?.id)
    ? `/dashboard/classes?enterprise=${meta.enterprise.id}`
    : '/dashboard/classes';

  return (
    <div className="aiq-dash dash-page">
      {/* class scope：返回班级列表（多班企业可切换班级）。enterprise scope 无列表可返。 */}
      {!isEnt && (
        <Link to={backTo} className="dash-back">← 返回班级列表</Link>
      )}
      <div className="dash-head">
        <h1 className="dash-h1">{title}</h1>
        <p className="dash-sub">{subtitle}</p>
      </div>

      {/* 子页导航 + 下载按钮同行 */}
      <div className="dash-toolbar dash-toolbar--split">
        <div className="dash-tabs">
          {tabs.map(([suffix, label]) => (
            <Link key={label} to={`${basePath}${suffix}`}
              className={`dash-tab${suffix === '' ? ' is-active' : ''}`}>
              {label}
            </Link>
          ))}
        </div>

        {/* 下载按钮 — 仅 class scope + 有 reco 权限时显示 */}
        {!isEnt && canAccessTrainingReco(user) && (
          <div className="dash-toolbar">
            <button
              className="dash-btn dash-btn--pri"
              disabled={dlClassLoading}
              onClick={async () => {
                if (dlClassLoading) return;
                setDlError(null);
                setDlClassLoading(true);
                const result = await downloadClassPdf(id);
                if (mountedRef.current) {
                  setDlClassLoading(false);
                  if (result && !result.ok) setDlError(result.error || 'PDF 下载失败');
                }
              }}
            >
              {dlClassLoading ? '下载中…' : '下载团队报告'}
            </button>
            <button
              className="dash-btn dash-btn--sec"
              disabled={dlEntLoading}
              onClick={async () => {
                if (dlEntLoading) return;
                setDlError(null);
                setDlEntLoading(true);
                const result = await downloadEnterprisePdf(data.class.enterprise.id);
                if (mountedRef.current) {
                  setDlEntLoading(false);
                  if (result && !result.ok) setDlError(result.error || 'PDF 下载失败');
                }
              }}
            >
              {dlEntLoading ? '下载中…' : '下载企业总报告'}
            </button>
            {dlError && <span style={{ fontSize: 12, color: 'var(--da-bad)' }}>{dlError}</span>}
          </div>
        )}

        {/* 下载按钮 — enterprise scope（无班级企业总报告 R3，封面=企业版）+ 有 reco 权限时显示 */}
        {isEnt && canAccessTrainingReco(user) && (
          <div className="dash-toolbar">
            <button
              className="dash-btn dash-btn--pri"
              disabled={dlEntLoading}
              onClick={async () => {
                if (dlEntLoading) return;
                setDlError(null);
                setDlEntLoading(true);
                const result = await downloadEnterprisePdf(id);
                if (mountedRef.current) {
                  setDlEntLoading(false);
                  if (result && !result.ok) setDlError(result.error || 'PDF 下载失败');
                }
              }}
            >
              {dlEntLoading ? '下载中…' : '下载企业总报告'}
            </button>
            {dlError && <span style={{ fontSize: 12, color: 'var(--da-bad)' }}>{dlError}</span>}
          </div>
        )}
      </div>

      {/* KPI 卡 — 团队均分 hero, 其余完成度拆开避免"未完成/中"歧义 */}
      <div className="dash-kpi">
        <div className="dash-tile dash-tile--hero">
          <p className="dash-tile__k">团队均分</p>
          <p className="dash-tile__v">{team_avg_score ?? '—'}</p>
          <p className="dash-tile__cap">满分 100 · {completion.total} 人参与</p>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">总人数</p>
          <p className="dash-tile__v">{completion.total}</p>
          <span className="dash-st dash-st--teal"><span className="dash-dot" />全部成员</span>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">已完成</p>
          <p className="dash-tile__v">{completion.completed}</p>
          <span className="dash-st dash-st--good"><span className="dash-dot" />{donePct}%</span>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">进行中</p>
          <p className="dash-tile__v">{completion.in_progress}</p>
          <span className="dash-st dash-st--warn"><span className="dash-dot" />作答中</span>
        </div>
        <div className="dash-tile">
          <p className="dash-tile__k">未开始</p>
          <p className="dash-tile__v">{completion.not_started}</p>
          <span className="dash-st dash-st--muted"><span className="dash-dot" />待邀请</span>
        </div>
      </div>

      {/* 维度团队均分 — dim_config_hardening:维度数会变,标题不写死 8;
          没有同行业基准数据,移除 industry_baseline 红线和脚注 */}
      <div className="dash-card">
        <div className="dash-sec"><h2>维度团队均分</h2><span className="dash-meta">满分 100</span></div>
        <div className="dash-dims">
          {dimension_avgs.map((d) => (
            <div key={d.code} className="dash-dim">
              <div className="dash-dim__nm">{d.name}</div>
              <div className="dash-dim__tk">
                {d.avg_score != null && (
                  <div className="dash-dim__fl" style={{ width: `${d.avg_score}%` }} />
                )}
              </div>
              <div className="dash-dim__vl">{d.avg_score ?? '—'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 总分分布 */}
      <div className="dash-card">
        <div className="dash-sec"><h2>总分分布</h2><span className="dash-meta">最高档 {maxBucketCount} 人</span></div>
        <div className="dash-chart">
          {buckets.map((b) => {
            const label = b.range || b.bucket;
            const count = b.count || 0;
            const ratio = count / maxBucketCount;
            // 最高柱留 22px 给上方 count 数字 + gap, 避免越界遮 KPI 卡边界
            const barHeight = count ? Math.max(20, Math.round(ratio * 110)) : 3;
            const tone = scoreBucketTone(label);
            return (
              <div key={label} className="dash-bar-col" title={`${label}: ${count} 人`}>
                {/* bar absolute bottom:0 贴 axis, count 数字 absolute 浮 bar 顶上方, 与 bar 高度解耦。 */}
                <div className="dash-bar-wrap">
                  <div className="dash-bar" style={{
                    height: barHeight,
                    background: count ? tone.fill : '#eef2f8',
                    borderColor: count ? tone.stroke : '#cbd5e1',
                    borderWidth: 1,
                    borderStyle: 'solid',
                  }} />
                  <div className="dash-bar-n" style={{ bottom: barHeight + 6, color: count ? undefined : '#94a3b8' }}>
                    {count}
                  </div>
                </div>
                <div className="dash-bar-x">{label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
